import { toJS } from 'mobx'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import { PaginationBody } from '@Entities/pagination'
import { ResultPage } from '@Entities/result-page'
import { CatalogTransferView } from '@Entities/rightsholder/catalog-transfer-view'
import CatalogTransferTabStore from '@App/stores/rightsholder/catalogTransfer/catalog-transfer-tab-store'

export const CATALOG_TRANSFER_QUERY_KEY = 'catalog-transfer-list'

/**
 * Runs a transfer tab's search through TanStack Query. The tab store owns everything the server sees (terms, filter,
 * page, page size) plus a data version it bumps to force a refetch, and the key is built from them, so a submit, a
 * page change and "the transfers changed elsewhere" are all just store writes.
 *
 * The results are copied into the store by `applyData` (from the query's data, not the fetch), so a cache hit — which
 * never runs the fetch — reaches the table, which reads the store, too.
 */
export const useCatalogTransferSearch = (store: CatalogTransferTabStore) => {
    const query = toJS(store.currentQuery)
    const filter = toJS(store.currentFilter)
    const pagination: PaginationBody = { page: store.pagination.page ?? 1, limit: store.limit }

    const transfersQuery = useSearchQuery<ResultPage<CatalogTransferView>>({
        queryKey: [CATALOG_TRANSFER_QUERY_KEY, ...store.searchScopeKey, store.dataVersion, query, filter, pagination],
        queryFn: () => store.fetchCatalogTransfers(query, filter, pagination),
        applyData: (page) => store.applyResults(page, pagination),
        onError: (error) => store.reportSearchError(error),
    })

    return transfersQuery
}

export default useCatalogTransferSearch
